import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PlaySpokenButton } from './play-spoken-button';

/**
 * Marks a line the voice agent says as SUGGESTED (ADR-342): built from the
 * record and following it, not written by the practice. A line the practice
 * wrote carries no badge. Used by the spoken name (providers) and the spoken
 * address (locations), in the detail sheets and beside the editor's label.
 */
export function SuggestedBadge({
  from,
}: {
  /** What the suggestion is built from, e.g. "the name and role". */
  from: string;
}): React.ReactElement {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="secondary" tabIndex={0} className="cursor-default font-normal">
            Suggested
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          Built from {from}, and updated when they change. Edit it to change how it&rsquo;s said.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * A detail-row value, with the Suggested badge before it when it is one and a
 * button to hear it after it (ADR-344). Null when there is nothing to say.
 */
export function SpokenValue({
  value,
  suggested,
  from,
  label,
}: {
  value: string | null | undefined;
  suggested: boolean;
  from: string;
  /** What the line is, for the play button's screen-reader label, e.g. "spoken name". */
  label: string;
}): React.ReactElement | null {
  if (!value) return null;
  return (
    <span className="inline-flex items-center justify-end gap-2">
      {suggested && <SuggestedBadge from={from} />}
      <span>{value}</span>
      <PlaySpokenButton text={value} label={label} />
    </span>
  );
}
